import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Zap, GitBranch, Mail, KeyRound,
  AlertTriangle, CheckCircle2, Edit3, Trash2, Send, X, Save,
} from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import apiClient from '../utils/apiClient';
import { TemplateEditorModal } from './automation/TemplatesTab';
// Custom Email Templates moved to Settings → Communication 2026-06-03 —
// those are operator-launched, not rule-triggered, so they belong with
// the Compose page. Import is re-added in CommunicationSection.js (Phase 2).
import GateTab from './automation/GateTab';

/**
 * Automation section — embedded inside Settings (/settings?tab=automation).
 *
 * Internal sub-nav (4 tabs) driven by ?view=... so deep-links work:
 *   /settings?tab=automation                          -> Rules (default)
 *   /settings?tab=automation&view=events              -> Events catalog
 *   /settings?tab=automation&view=templates           -> Rule-based email templates
 *   /settings?tab=automation&view=gate                -> Gate config
 *
 * The Rules tab is the day-to-day surface; it reads/writes the 13
 * (now 14) seeded automation_rules rows the dispatcher fires off.
 *
 * Custom (operator-authored) templates moved to Settings -> Communication
 * 2026-06-03 — those are operator-launched bulk sends, not rule-triggered,
 * so they belong with the Compose page.
 */

// "Email wording" WAS deliberately hidden from this strip (the theory: a rule
// and its email are one thing, so the only door should be the template key in
// a rule row). In practice the operator would land on the hidden view with NO
// tab highlighted, click back to Notifications, and see a different table —
// which read as the app flip-flopping between two versions of the templates
// screen. Reported as exactly that on 2026-07-29. A view you can stand on
// must be a tab you can see; the rule-row template key remains a shortcut in.
const SUB_TABS = [
  { key: 'events',           label: 'Events',                       icon: Zap,        desc: 'Read-only catalog of dispatchable events' },
  { key: 'rules',            label: 'Active Notifications',         icon: GitBranch,  desc: 'The rules currently firing — when each sends and what it says' },
];

// Views reachable by deep-link but NOT shown in this tab strip.
// - templates: opened from a rule row (the rule and its wording are one thing)
// - gate:      lives under Settings → Facility → Gate Automation. It is
//              facility hardware setup (which controller, code length, how a
//              tenant's code is generated), not a notification rule, and
//              listing it here as well put the same screen in two places.
// The "All Notifications" tab was retired 2026-07-29 at the operator's
// suggestion ("active notifications sort of already capture everything").
// One list now owns the whole notification: unruled templates render as
// greyed rows under the rules, wording opens inline, and old
// ?view=templates deep-links land here. TemplatesTab survives only as the
// home of TemplateEditorModal.
// Gate is NOT a view here. It moved to /settings/gate under Facility —
// rendering it inside Communication too put facility hardware setup in the
// middle of tenant messaging. An old ?view=gate link redirects (below).


// ============================================================================
// Events catalog — hardcoded (events are code-defined, not DB)
// ============================================================================
const EVENTS = [
  { name: 'customer_created',       category: 'lifecycle',   carries: ['customer_id'] },
  { name: 'lease_signed',           category: 'lifecycle',   carries: ['rental_id', 'customer_id'] },
  { name: 'gate_code_provisioned',  category: 'gate',        carries: ['rental_id', 'gate_code'] },
  { name: 'gate_code_pending',      category: 'gate',        carries: ['rental_id'] },
  { name: 'gate_code_failed',       category: 'gate',        carries: ['rental_id', 'customer_id'] },
  { name: 'payment_received',       category: 'billing',     carries: ['rental_id', 'customer_id', 'amount'] },
  { name: 'autopay_failed',         category: 'billing',     carries: ['rental_id', 'charge_id'] },
  { name: 'invoice_created',        category: 'billing',     carries: ['rental_id', 'invoice_id'] },
  { name: 'rental_created',         category: 'lifecycle',   carries: ['rental_id', 'customer_id'] },
  { name: 'rental_ended',           category: 'lifecycle',   carries: ['rental_id', 'customer_id'] },
  // time_based_nudge removed 2026-08-07 — the nudge workflow is retired;
  // stalled signups surface in Leads and Reservations instead.
  { name: 'rent_due_minus_n',       category: 'time-based',  carries: ['rental_id', 'due_date'] },
  { name: 'rent_due_plus_n',        category: 'time-based',  carries: ['rental_id', 'due_date'] },
];


const CATEGORY_BADGE = {
  'lifecycle':  'bg-blue-50 text-blue-700 ring-1 ring-blue-200',
  'gate':       'bg-violet-50 text-violet-700 ring-1 ring-violet-200',
  'billing':    'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  'time-based': 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
};


// ============================================================================
// Legacy duplicate rule_keys — flagged "Recommended to disable"
// ----------------------------------------------------------------------------
// Legacy duplicate rule keys from pre-canonical seeds. These overlap with the
// consolidated move-in email and would double-send. Operator opts-in to
// disable; we never auto-disable.
// ============================================================================
const LEGACY_DUPLICATE_RULE_KEYS = new Set([
  'welcome',
  'rental_confirmed',
  'lease_executed',
  'lease_signed_confirmation',
  'gate_code',
  'gate_code_welcome',
  'gate_code_pending',
  'tenant_gate_code_pending',
  'complete_your_rental_nudge',
]);


// ============================================================================
// EventsTab — read-only catalog
// ============================================================================
const EventsTab = () => (
  <div>
    <p className="text-[13px] text-slate-500 mb-4 max-w-3xl">
      Events fire when the system does things. Rules react to events. You can&apos;t edit
      events directly — they&apos;re hard-wired to the engine.
    </p>
    <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left font-medium px-4 py-2.5">Event Name</th>
              <th className="text-left font-medium px-4 py-2.5">Category</th>
              <th className="text-left font-medium px-4 py-2.5">Context Fields</th>
            </tr>
          </thead>
          <tbody>
            {EVENTS.map((e) => (
              <tr key={e.name} className="border-t border-slate-100">
                <td className="px-4 py-2.5 font-mono text-[12px] text-slate-800">{e.name}</td>
                <td className="px-4 py-2.5">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${CATEGORY_BADGE[e.category] || 'bg-slate-100 text-slate-700'}`}>
                    {e.category}
                  </span>
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex flex-wrap gap-1">
                    {e.carries.map((f) => (
                      <span key={f} className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-mono text-[11px]">
                        {f}
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);


// ============================================================================
// RulesTab — fetches /api/facilities/{fid}/automation-rules
// ============================================================================

const RuleEditModal = ({ rule, onClose, onSaved, facilityId }) => {
  const [form, setForm] = useState({
    enabled: rule.enabled ?? true,
    channel: rule.channel || 'email',
    template_key: rule.template_key || '',
    conditions: JSON.stringify(rule.conditions || {}, null, 2),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    setError(null);
    let parsedConditions;
    try {
      parsedConditions = JSON.parse(form.conditions || '{}');
    } catch (e) {
      setError('Conditions must be valid JSON.');
      return;
    }
    setSaving(true);
    try {
      const res = await apiClient.patch(
        `/api/facilities/${facilityId}/automation-rules/${rule._id || rule.id}`,
        {
          enabled: form.enabled,
          channel: form.channel,
          template_key: form.template_key || null,
          conditions: parsedConditions,
        },
      );
      onSaved(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div>
            <h3 className="text-[14px] font-semibold text-slate-900">{rule.name || rule.rule_key}</h3>
            <p className="text-[11px] text-slate-400 font-mono">{rule.rule_key}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {error && (
            <div className="rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-[12px] text-rose-700 flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5" /> {error}
            </div>
          )}

          <label className="flex items-center gap-2 text-[13px] text-slate-700">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
              className="rounded border-slate-300"
            />
            Enabled
          </label>

          <div>
            <label className="block text-[12px] font-medium text-slate-700 mb-1">Channel</label>
            <select
              value={form.channel}
              onChange={(e) => setForm({ ...form, channel: e.target.value })}
              className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-[13px]"
            >
              <option value="email">email</option>
              <option value="sms">sms</option>
              <option value="both">both</option>
              <option value="none">none (silent / lifecycle marker)</option>
            </select>
          </div>

          <div>
            <label className="block text-[12px] font-medium text-slate-700 mb-1">Template key</label>
            <input
              type="text"
              value={form.template_key || ''}
              onChange={(e) => setForm({ ...form, template_key: e.target.value })}
              placeholder="e.g. rental_confirmed_with_gate_code"
              className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-[13px] font-mono"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Must match a template registered in services/email_templates.py
            </p>
          </div>

          <div>
            <label className="block text-[12px] font-medium text-slate-700 mb-1">Conditions (JSON)</label>
            <textarea
              value={form.conditions}
              onChange={(e) => setForm({ ...form, conditions: e.target.value })}
              rows={8}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-[12px] font-mono focus:outline-none focus:border-blue-500"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Example: <code>{'{ "min_balance_cents": 100, "skip_if_autopay": true }'}</code>
            </p>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-3 py-1.5 text-[13px] text-slate-700 hover:bg-slate-50 rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md text-[13px] font-medium"
          >
            <Save className="h-3.5 w-3.5" /> {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};


// Templates that no RULE fires but a CODE PATH sends — named so the row can
// say who, instead of the false "Nothing sends this".
const CODE_SENDERS = {
  lien_notice: 'Sent by the lien workflow',
  // Sent by routers/rate_changes.py when the operator schedules a rate
  // change — it said "Nothing sends this", which was false, the same
  // mislabel that hid two genuinely dead notifications.
  rate_change_notice: 'Sent when you schedule a rate change',
};

const RulesTab = ({ facilityId }) => {
  const [rules, setRules] = useState([]);
  // The full template registry, merged under the rules: ONE list owns the
  // whole notification — whether it fires, on which channels, and its
  // wording. A separate "All Notifications" list holding just the unruled
  // templates confused the operator three separate times in one day
  // ("flip-flopping", "a lot more templates", "why do we need All").
  const [registry, setRegistry] = useState([]);
  const [wordingFor, setWordingFor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [info, setInfo] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Jump to a rule's email wording (the Email wording view, preselected).
  //
  // THIS FUNCTION MUST LIVE IN THIS COMPONENT. The template-name button below
  // calls it, and an identically-named helper in the parent AutomationSection
  // is NOT in scope here — for weeks every click threw
  // "ReferenceError: openTemplate is not defined" and silently did nothing.
  // The operator reported it as "I click and it just dies"; it shipped
  // because nothing anywhere renders this table in a real browser and
  // clicks it. Proven (and re-proven fixed) with a Playwright run against
  // the production build, which is the only reason the ReferenceError was
  // ever seen.
  const openTemplate = (templateKey) => {
    const params = new URLSearchParams(searchParams);
    params.set('view', 'templates');
    // Remember the door the operator came through: closing the editor
    // returns them here instead of stranding them on the All list.
    params.set('from', 'rules');
    if (templateKey) params.set('template', templateKey);
    setSearchParams(params, { replace: true });
  };

  const openWording = (templateKey) => {
    const t = registry.find((x) => x.template_key === templateKey);
    if (t) setWordingFor(t);
  };

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(`/api/facilities/${facilityId}/automation-rules/`);
      setRules(Array.isArray(res.data) ? res.data : []);
      try {
        const tr = await apiClient.get(
          `/api/facilities/${facilityId}/templates/`, { params: { kind: 'system' } });
        setRegistry(Array.isArray(tr.data) ? tr.data : []);
      } catch { setRegistry([]); }
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load rules.');
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  // Per-channel switches. rule.channel is one value (email|sms|both|none);
  // the operator thinks in two independent facts — "email them?" and "text
  // them?" — so the row shows two switches and composes the stored value.
  // Asked for after the operator found SMS reminders "a bit much" and had no
  // per-rule way to turn just the texts off.
  const setChannels = async (rule, emailOn, smsOn) => {
    const channel = emailOn && smsOn ? 'both' : emailOn ? 'email' : smsOn ? 'sms' : 'none';
    try {
      const res = await apiClient.patch(
        `/api/facilities/${facilityId}/automation-rules/${rule._id || rule.id}`,
        { channel },
      );
      setRules((rs) => rs.map((r) => ((r._id || r.id) === (rule._id || rule.id) ? res.data : r)));
      setInfo(`${rule.name || rule.rule_key}: ${channel === 'both' ? 'email + text' : channel === 'none' ? 'nothing sends (rule still on)' : channel + ' only'}.`);
      setTimeout(() => setInfo(null), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Channel change failed.');
    }
  };

  const toggleEnabled = async (rule) => {
    try {
      const res = await apiClient.patch(
        `/api/facilities/${facilityId}/automation-rules/${rule._id || rule.id}`,
        { enabled: !rule.enabled },
      );
      setRules((rs) => rs.map((r) => ((r._id || r.id) === (rule._id || rule.id) ? res.data : r)));
      setInfo(`${rule.name || rule.rule_key} ${res.data.enabled ? 'enabled' : 'disabled'}.`);
      setTimeout(() => setInfo(null), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Toggle failed.');
    }
  };

  const handleDelete = async (rule) => {
    if (!LEGACY_DUPLICATE_RULE_KEYS.has(rule.rule_key)) {
      setError('Delete is only available for rules flagged as dead/dup. Disable instead.');
      return;
    }
    if (!window.confirm(`Delete rule "${rule.name || rule.rule_key}"? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/api/facilities/${facilityId}/automation-rules/${rule._id || rule.id}`);
      setRules((rs) => rs.filter((r) => (r._id || r.id) !== (rule._id || rule.id)));
      setInfo('Rule deleted.');
      setTimeout(() => setInfo(null), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Delete failed (endpoint may not exist).');
    }
  };


  if (loading) return <p className="text-[13px] text-slate-500">Loading rules…</p>;

  return (
    <div>
      {error && (
        <div className="mb-3 rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-[12px] text-rose-700 flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5" /> {error}
        </div>
      )}
      {info && (
        <div className="mb-3 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-[12px] text-emerald-700 flex items-center gap-2">
          <CheckCircle2 className="h-3.5 w-3.5" /> {info}
        </div>
      )}

      <div className="rounded-xl border border-slate-200/80 bg-white overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left font-medium px-3 py-2.5 w-20">Enabled</th>
              <th className="text-left font-medium px-3 py-2.5">Rule</th>
              <th className="text-left font-medium px-3 py-2.5">Listens to</th>
              <th className="text-left font-medium px-3 py-2.5">Sends template</th>
              <th className="text-left font-medium px-3 py-2.5 w-36">Email / Text</th>
              <th className="text-left font-medium px-3 py-2.5">Lifecycle</th>
              <th className="text-left font-medium px-3 py-2.5 w-44">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-slate-400 text-[12px]">
                  No automation rules seeded yet for this facility.
                </td>
              </tr>
            )}
            {rules.map((rule) => {
              const id = rule._id || rule.id;
              const isDead = LEGACY_DUPLICATE_RULE_KEYS.has(rule.rule_key);
              return (
                <tr key={id} className="border-t border-slate-100 align-top">
                  <td className="px-3 py-2.5">
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!rule.enabled}
                        onChange={() => toggleEnabled(rule)}
                        className="rounded border-slate-300"
                      />
                    </label>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="font-medium text-slate-900">{rule.name || rule.rule_key}</div>
                    <div className="text-[11px] text-slate-400 font-mono">{rule.rule_key}</div>
                    {isDead && (
                      <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 ring-1 ring-rose-200 text-[10px] font-medium">
                        <AlertTriangle className="h-3 w-3" /> Recommended to disable
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-[12px] text-slate-700">
                    {rule.trigger_on}
                    {rule.offset_days ? (
                      <span className="text-slate-400"> ({rule.offset_days > 0 ? '+' : ''}{rule.offset_days}d)</span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-[12px]">
                    {/* The template key used to be a dead label here: you could
                        see WHICH email a rule sends but not read or edit it
                        without hunting for it in a separate tab. One row now
                        owns the whole notification — when it fires, whether
                        it's on, which channel, and its wording. */}
                    {rule.template_key ? (
                      <button
                        type="button"
                        onClick={() => openWording(rule.template_key)}
                        className="text-blue-600 hover:text-blue-700 hover:underline font-mono text-[12px]"
                        title="Edit this notification's wording"
                      >
                        {rule.template_key}
                        {registry.find((t) => t.template_key === rule.template_key)?.has_override && (
                          <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 ring-1 ring-amber-200 no-underline">Customized</span>
                        )}
                      </button>
                    ) : (
                      <span className="text-slate-400" title="Silent rule — no email">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    {(() => {
                      const emailOn = rule.channel === 'email' || rule.channel === 'both';
                      const smsOn = rule.channel === 'sms' || rule.channel === 'both';
                      const Mini = ({ on, label, next }) => (
                        <button
                          type="button" role="switch" aria-checked={on}
                          onClick={next}
                          title={`${on ? 'Stop' : 'Start'} sending ${label.toLowerCase()} for this rule`}
                          className="inline-flex items-center gap-1 mr-2"
                        >
                          <span className="relative inline-block w-7 h-4 rounded-full"
                                style={{ backgroundColor: on ? '#059669' : '#cbd5e1' }}>
                            <span className="absolute top-0.5 h-3 w-3 rounded-full bg-white shadow"
                                  style={{ left: on ? '14px' : '2px' }} />
                          </span>
                          <span className="text-[11px] text-slate-600">{label}</span>
                        </button>
                      );
                      return (
                        <div className="flex items-center">
                          <Mini on={emailOn} label="Email"
                                next={() => setChannels(rule, !emailOn, smsOn)} />
                          <Mini on={smsOn} label="Text"
                                next={() => setChannels(rule, emailOn, !smsOn)} />
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-2.5 text-[12px] text-slate-600">
                    {rule.lifecycle_stage || <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1">
                      {isDead && !rule.enabled === false && rule.enabled && (
                        <button
                          onClick={() => toggleEnabled(rule)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-rose-600 hover:bg-rose-700 text-white rounded"
                          title="One-click disable (recommended)"
                        >
                          Disable
                        </button>
                      )}
                      <button
                        onClick={() => setEditing(rule)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] text-slate-700 hover:bg-slate-100 rounded"
                      >
                        <Edit3 className="h-3 w-3" /> Edit
                      </button>
                      {isDead && (
                        <button
                          onClick={() => handleDelete(rule)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] text-rose-600 hover:bg-rose-50 rounded"
                          title="Delete (allowed because rule is dead/dup)"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {/* Templates no rule fires. Code-sent templates (lien workflow,
                rate changes) are HIDDEN here per owner 2026-07-29 — this
                screen lists the automatic notifications an operator can
                toggle, and those two are sent by their own features. Their
                code-default wording still renders fine; overriding it now
                requires the templates API (no UI entry — deliberate).
                Anything ELSE unruled still renders "Nothing sends this" —
                the tripwire that exposed two dead notifications. */}
            {registry
              .filter((t) => !rules.some((r) => r.template_key === t.template_key))
              .filter((t) => !CODE_SENDERS[t.template_key])
              .map((t) => (
                <tr key={`tpl-${t.template_key}`} className="border-t border-slate-100 bg-slate-50/40">
                  <td className="px-3 py-2.5"></td>
                  <td className="px-3 py-2.5">
                    <div className="font-medium text-slate-500">{t.name || t.template_key}</div>
                    <div className="text-[11px] text-slate-400 font-mono">{t.template_key}</div>
                  </td>
                  <td className="px-3 py-2.5 text-[12px] text-slate-400">
                    {CODE_SENDERS[t.template_key] || 'Nothing sends this'}
                  </td>
                  <td className="px-3 py-2.5">
                    <button
                      type="button"
                      onClick={() => openWording(t.template_key)}
                      className="text-blue-600 hover:text-blue-700 hover:underline font-mono text-[12px]"
                      title="Edit this wording"
                    >
                      {t.template_key}
                    </button>
                    {t.has_override && (
                      <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 ring-1 ring-amber-200">Customized</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5"></td>
                  <td className="px-3 py-2.5 text-[12px] text-slate-400">—</td>
                  <td className="px-3 py-2.5"></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {wordingFor && (
        <TemplateEditorModal
          template={wordingFor}
          facilityId={facilityId}
          onClose={() => setWordingFor(null)}
          onSaved={() => { setWordingFor(null); load(); }}
        />
      )}

      {editing && (
        <RuleEditModal
          rule={editing}
          facilityId={facilityId}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setRules((rs) => rs.map((r) => ((r._id || r.id) === (updated._id || updated.id) ? updated : r)));
            setEditing(null);
            setInfo('Rule updated.');
            setTimeout(() => setInfo(null), 3000);
          }}
        />
      )}
    </div>
  );
};


// ============================================================================
// AutomationSection — top-level
// ============================================================================
const AutomationSection = ({ embedded = false }) => {
  const { currentFacility } = useFacility();
  const facilityId = currentFacility?._id || currentFacility?.id || '';
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Legacy deep link: gate used to be a view in this section. It now lives at
  // /settings/gate under Facility — rendering it here as well put facility
  // hardware setup in the middle of tenant messaging.
  useEffect(() => {
    if (searchParams.get('view') === 'gate') {
      navigate('/settings/gate', { replace: true });
    }
  }, [searchParams, navigate]);

  const rawView = searchParams.get('view');
  const ALL_VIEWS = SUB_TABS;
  const legacyView = rawView === 'templates' ? 'rules' : rawView;
  const activeKey = legacyView && ALL_VIEWS.find((t) => t.key === legacyView) ? legacyView : 'rules';
  const activeTab = ALL_VIEWS.find((t) => t.key === activeKey)
    || SUB_TABS.find((t) => t.key === 'rules');

  // Jump straight to a rule's email wording. Keeps the template key in the
  // URL so the editor can open that row directly and the link is shareable.
  const openTemplate = (templateKey) => {
    const params = new URLSearchParams(searchParams);
    params.set('view', 'templates');
    // Remember the door the operator came through: closing the editor
    // returns them here instead of stranding them on the All list.
    params.set('from', 'rules');
    if (templateKey) params.set('template', templateKey);
    setSearchParams(params, { replace: true });
  };

  const setTab = (key) => {
    const params = new URLSearchParams(searchParams);
    if (key === 'rules') params.delete('view');
    else params.set('view', key);
    setSearchParams(params, { replace: true });
  };

  return (
    <div>
      {embedded && (
        <p className="text-[12px] text-slate-400 mb-4">
          {activeTab.desc}
          {currentFacility?.name && <> &middot; {currentFacility.name}</>}
        </p>
      )}

      <div className="border-b border-slate-200 mb-5">
        <nav className="-mb-px flex gap-6 overflow-x-auto" aria-label="Automation sub-tabs">
          {/* ALL_VIEWS, not SUB_TABS: "Email wording" is a real view — a
              template-key click lands there — but it was missing from this
              strip. The operator would arrive on it with NO tab highlighted
              and no way back except clicking another tab, so the section
              read as flip-flopping between two different template tables.
              A view you can stand on must be a tab you can see. */}
          {ALL_VIEWS.map((t) => {
            const isActive = activeKey === t.key;
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap py-2.5 px-1 border-b-2 text-[13px] font-medium transition-colors ${
                  isActive
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeKey === 'events' && <EventsTab />}
      {activeKey === 'rules' && <RulesTab facilityId={facilityId} />}
      {activeKey === 'gate' && <GateTab facilityId={facilityId} />}
    </div>
  );
};

export default AutomationSection;
